<template>
  <div>
    <v-text-field
      density="compact"
      rounded
      min-width="200"
      clear-icon="$x"
      :placeholder="$t('general.search')"
      :aria-label="$t('general.search')"
      prepend-inner-icon="$search"
      data-testid="select-hierarchy-search"
      clearable
      variant="filled"
      class="search-filter mb-4"
      bg-color="indigo-faint"
      hide-details
      single-line
      @update:model-value="search = $event"
    />
    <table-wrapper>
      <v-data-table-virtual
        :headers="headers"
        :items="globalPolicies"
        item-selectable="selectable"
        item-value="id"
        return-object
        :items-per-page="pagination.pageSize"
        :items-length="total"
        show-select
        select-strategy="single"
        disable-sort
        fixed-header
        v-model="selectedItems"
        :loading="loading"
      >
        <template #item="{ item, index, internalItem, isSelected, toggleSelect }">
          <tr
            :key="index"
            :id="index.toString()"
            :data-testid="`table-row-${index}`"
            v-intersect="onIntersect"
            class="table-row"
            :class="{ 'v-row--selected': isSelected(internalItem) }"
          >
            <td class="w-5">
              <v-checkbox-btn
                :model-value="isSelected(internalItem)"
                color="primary"
                density="compact"
                @update:model-value="toggleSelect(internalItem)"
                true-icon="$radioMarked"
                false-icon="$radio"
              ></v-checkbox-btn>
            </td>
            <td class="w-50">
              {{ item.name }}
            </td>
            <td class="policy-name-col">
              {{ item.assignedWorkspaceCount }}
            </td>
            <td class="labels-col">
              {{ getFormattedDateTime(item.modifiedAt, "ddd, MMM D YYYY, hh:mm A") }}
            </td>
          </tr>
        </template>
      </v-data-table-virtual>
    </table-wrapper>
  </div>
</template>

<script lang="ts">
import { onMounted, type PropType, type Ref, ref, watch } from "vue";
import { handleVirtualScrollData } from "@coronet/cui";
import { getFormattedDateTime } from "@/_helpers/date";
import type { Pagination } from "@/types.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import { useI18n } from "vue-i18n";
import {
  type GlobalPolicyListItem,
  useMspGlobalPoliciesStore,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import TableWrapper from "@/components/TableWrapper.vue";
import debounce from "lodash/debounce";
import type { BaseMspWorkspaceListItem } from "@/_store/msp/msp.module.ts";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";

export default {
  components: {
    TableWrapper,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<BaseMspWorkspaceListItem[]>>,
      required: true,
    },
  },
  emits: ["update:localValue"],
  setup(props, { emit }) {
    const globalPolicyStore = useMspGlobalPoliciesStore();
    const search: Ref<string> = ref("");
    const total = ref(0);
    const selectedItems = ref<GlobalPolicyListItem[]>([]);
    const globalPolicies = ref<GlobalPolicyListItem[]>([]);
    const pagination = ref<Pagination>({
      page: 0,
      pageSize: 10,
    });
    const i18n = useI18n();
    const loading = ref(false);
    const headers = ref([
      { title: i18n.t("modals.assignGlobalPolicy.policyName"), key: "name" },
      { title: i18n.t("modals.assignGlobalPolicy.workspaces"), key: "assignedWorkspaceCount" },
      { title: i18n.t("modals.assignGlobalPolicy.lastModified"), key: "modifiedAt" },
    ]);

    const loadMorePolicies = async () => {
      pagination.value = {
        page: pagination.value.page + 1,
        pageSize: 10,
      };
      await getItems();
    };

    const getItems = async () => {
      loading.value = true;
      try {
        const { data } = await globalPolicyStore.searchGlobalPolicies(
          search.value,
          pagination.value
        );
        globalPolicies.value = handleVirtualScrollData(globalPolicies.value, data.items, "id");
        total.value = data.total;
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    };

    const { onIntersect } = useInfiniteScroll(globalPolicies, { total }, loadMorePolicies);

    watch(
      () => selectedItems.value,
      (newVal) => {
        if (newVal.length) {
          emit("update:localValue", {
            policyId: newVal[0].id!,
            workspaces: props.config.item,
          });
        }
      }
    );

    watch(
      () => search.value,
      debounce(async () => {
        globalPolicies.value = [];
        await getItems();
      }, 300)
    );

    onMounted(async () => {
      await getItems();
    });

    return {
      loading,
      total,
      pagination,
      onIntersect,
      headers,
      selectedItems,
      search,
      globalPolicies,
      getFormattedDateTime,
      loadMorePolicies,
    };
  },
};
</script>

<style lang="scss" scoped>
:deep(*) {
  .v-field__outline {
    display: none !important;
  }

  .v-table__wrapper {
    height: 400px !important;
    border: 1px solid rgb(var(--v-theme-indigo-faint));
  }

  .v-table {
    box-shadow: unset !important;
  }
}

.table-row {
  height: 68px;
}

.v-row--selected {
  background-color: rgb(var(--v-theme-indigo-pale));
  td {
    border-bottom: 1px solid white !important;
  }
}
</style>
